import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useAuth } from '@/features/auth/hooks/useAuth';

function AuthLoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
    </div>
  );
}

function useOnboardingCheck() {
  const user = useAuthStore((s) => s.user);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  // Skip the incomplete-profile check when an admin is impersonating —
  // the admin shouldn't be forced into the target user's onboarding flow.
  if (isImpersonating) return false;
  const profileIncomplete = !user?.firstNameHe || !user?.lastNameHe || !user?.phone;
  // Also require onboarding if terms haven't been accepted yet (first login after approval)
  const needsOnboarding = profileIncomplete || !user?.termsAcceptedAt;
  return needsOnboarding;
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const profileIncomplete = useOnboardingCheck();

  // Keep user profile + permissions in sync with the DB
  useAuth();

  if (!hasHydrated) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (profileIncomplete && window.location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

export function AdminRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const permissionsLoaded = useAuthStore((s) => s.permissionsLoaded);
  const { canAccessAdmin } = usePermissions();
  const profileIncomplete = useOnboardingCheck();

  // Fire the /auth/me query to load permissions
  useAuth();

  if (!hasHydrated) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (profileIncomplete) {
    return <Navigate to="/onboarding" replace />;
  }

  // Wait for permissions to load before evaluating access
  if (!permissionsLoaded) {
    return <AuthLoadingSpinner />;
  }

  if (!canAccessAdmin()) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export function ManagerRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const permissionsLoaded = useAuthStore((s) => s.permissionsLoaded);
  const { isManager } = usePermissions();
  const profileIncomplete = useOnboardingCheck();

  // Fire the /auth/me query to load permissions
  useAuth();

  if (!hasHydrated) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (profileIncomplete) {
    return <Navigate to="/onboarding" replace />;
  }

  // Wait for permissions to load before evaluating access
  if (!permissionsLoaded) {
    return <AuthLoadingSpinner />;
  }

  if (!isManager()) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
